import { Alert, Button, Group, Loader, Modal, Stack, Text, Textarea } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAddComment } from "../../hooks/useCustomerViewActions.js";
import { agendaTitleOf } from "../../lib/customerView/agendaTitle.js";
import { useCreateProtocol } from "../../hooks/usePresentation.js";
import { closeStageWindow } from "../../lib/customerView/stageWindow.js";
import { toLocalDateString } from "../../lib/date.js";
import { usePresentationStore } from "../../stores/presentation.js";
import { useReviewedStore } from "../../stores/reviewed.js";

/**
 * Der Abschluss des Termins: das Ergebnisprotokoll als Markdown zum Kopieren
 * oder Herunterladen — und der einzige Weg, auf dem die privaten
 * Meeting-Notizen das Cockpit verlassen: als INTERNE Kommentare am jeweiligen
 * Ticket, auf ausdrücklichen Klick. Auf die Bühne kommen sie nie
 * (Sicherheitsinvariante); `buildStageSnapshot` kennt sie gar nicht.
 *
 * Das Fazit ist fail-soft: Fehlt es (`fazitGenerated: false`), steht das
 * Protokoll trotzdem — mit einem Hinweis, damit niemand rätselt, warum die
 * Schlussbetrachtung fehlt.
 */
export function ProtocolModal({
  opened,
  onClose,
  customerObjectKey,
  customerName,
}: {
  opened: boolean;
  onClose: () => void;
  customerObjectKey: string;
  customerName: string;
}) {
  const { t } = useTranslation();
  const deck = usePresentationStore((s) => s.deck);
  const agendaItems = usePresentationStore((s) => s.agendaItems);
  const notes = usePresentationStore((s) => s.notes);
  const clearNote = usePresentationStore((s) => s.clearNote);
  const reset = usePresentationStore((s) => s.reset);
  const reviewedAt = useReviewedStore((s) => s.reviewedAt);

  const protocol = useCreateProtocol();
  const addComment = useAddComment(customerObjectKey);
  const [postingNotes, setPostingNotes] = useState(false);

  const noteEntries = Object.entries(notes).filter(([, text]) => text.trim() !== "");
  const markdown = protocol.data?.markdown ?? "";
  /**
   * Nur die AKTIVIERTEN Punkte — abgewählte bleiben im Cockpit stehen, damit
   * der Lead sie wieder anschalten kann, standen aber nie im Termin. Was nicht
   * besprochen wurde, gehört nicht ins Ergebnisprotokoll.
   */
  const agendaForProtocol = useMemo(
    () =>
      agendaItems
        .filter((a) => a.enabled)
        // Leerer Titel? Dann der Vorgabetitel der Art. `agendaItemSchema`
        // lehnt einen leeren Titel ab (`.trim()` vor `min(1)`) — ohne
        // Rückfall scheiterte das Protokoll also erst im Aufruf, und zwar am
        // Ende des Termins, wenn niemand mehr sucht, woran es lag.
        .map((a) => ({ kind: a.kind, title: agendaTitleOf(a, t), body: a.body, enabled: a.enabled })),
    [agendaItems, t],
  );

  // Einmal je Öffnung erzeugen: Das Protokoll kostet einen LLM-Aufruf, ein
  // erneutes Öffnen des Dialogs soll ihn nicht wiederholen.
  const { mutate } = protocol;
  useEffect(() => {
    if (!opened || protocol.data || protocol.isPending || protocol.isError) return;
    mutate({
      customerObjectKey,
      customerName,
      issueKeys: deck,
      reviewedKeys: Object.keys(reviewedAt).filter((k) => deck.includes(k)),
      agendaItems: agendaForProtocol,
    });
  }, [opened, protocol.data, protocol.isPending, protocol.isError, mutate, customerObjectKey, customerName, deck, reviewedAt, agendaForProtocol]);

  /** Wie in `statusberichte.tsx`: Zugriff selbst kann in unsicheren Kontexten synchron werfen. */
  const copy = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(markdown);
      notifications.show({ color: "green", message: t("pages.customerView.present.copied") });
    } catch {
      notifications.show({ color: "red", message: t("pages.customerView.present.copyFailed") });
    }
  };

  const download = () => {
    const safeKey = customerObjectKey.replace(/[^\w.-]+/g, "_");
    const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `protokoll-${safeKey}-${toLocalDateString(new Date())}.md`;
    // Anhängen vor dem Klick: Firefox löst den Download sonst nicht zuverlässig
    // aus, wenn der Anker nie Teil des DOM war. Das Entfernen/Revoken erst nach
    // einem Tick — sonst bricht der Download in manchen Browsern noch ab.
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.remove();
    }, 0);
  };

  /**
   * Notizen GLEICHZEITIG posten (`Promise.allSettled`) statt der Reihe nach:
   * Ein Fehler bei einer Notiz darf die anderen nicht verzögern — und vor
   * allem nicht verschlucken. Nur die erfolgreich geposteten werden aus dem
   * Store entfernt (`clearNote`); fehlgeschlagene bleiben stehen, damit sie
   * beim nächsten Versuch erneut angeboten werden, statt verloren zu gehen.
   */
  const postNotes = async () => {
    setPostingNotes(true);
    const results = await Promise.allSettled(
      noteEntries.map(([key, text]) =>
        addComment.mutateAsync({
          key,
          text: `${t("pages.customerView.present.noteCommentPrefix")}\n${text.trim()}`,
          visibility: "internal",
        }),
      ),
    );
    let ok = 0;
    const failedKeys: string[] = [];
    results.forEach((result, i) => {
      const key = noteEntries[i]?.[0];
      if (key === undefined) return;
      if (result.status === "fulfilled") {
        ok++;
        clearNote(key);
      } else {
        failedKeys.push(key);
      }
    });
    if (ok > 0) {
      notifications.show({ color: "green", message: t("pages.customerView.present.notesPosted", { count: ok }) });
    }
    if (failedKeys.length > 0) {
      notifications.show({
        color: "red",
        message: t("pages.customerView.present.notePostFailed", { key: failedKeys.join(", ") }),
      });
    }
    setPostingNotes(false);
  };

  const finish = () => {
    closeStageWindow();
    reset();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      title={t("pages.customerView.present.protocolTitle")}
    >
      <Stack gap="sm">
        {protocol.isPending && (
          <Group gap="xs">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">{t("pages.customerView.present.protocolLoading")}</Text>
          </Group>
        )}

        {protocol.isError && (
          <Alert color="red" variant="light">
            <Group justify="space-between" gap="sm" wrap="wrap">
              <Text size="sm">{t("pages.customerView.present.protocolFailed")}</Text>
              <Button size="xs" variant="default" onClick={() => protocol.reset()}>
                {t("pages.customerView.present.protocolRetry")}
              </Button>
            </Group>
          </Alert>
        )}

        {protocol.data && (
          <>
            {!protocol.data.fazitGenerated && (
              <Alert color="yellow" variant="light">{t("pages.customerView.present.noFazit")}</Alert>
            )}
            <Textarea
              readOnly
              autosize
              minRows={10}
              maxRows={18}
              value={markdown}
              aria-label={t("pages.customerView.present.protocolTitle")}
              styles={{ input: { fontFamily: "monospace", fontSize: "0.8rem" } }}
            />
            <Group gap="sm" wrap="wrap">
              <Button size="xs" variant="default" onClick={() => void copy()}>
                {t("pages.customerView.present.copy")}
              </Button>
              <Button size="xs" variant="default" onClick={download}>
                {t("pages.customerView.present.download")}
              </Button>
            </Group>
          </>
        )}

        {noteEntries.length > 0 && (
          <Alert color="yellow" variant="light" title={t("pages.customerView.present.notesTitle", { n: noteEntries.length })}>
            <Stack gap="xs">
              <Text size="xs" c="dimmed">{t("pages.customerView.present.notesHint")}</Text>
              {noteEntries.map(([key, text]) => (
                <Text key={key} size="xs" lineClamp={2}>
                  <Text span ff="monospace" c="dimmed">{key}</Text>{` ${text.trim()}`}
                </Text>
              ))}
              <Group>
                <Button size="xs" loading={postingNotes} onClick={() => void postNotes()}>
                  {t("pages.customerView.present.postNotes", { n: noteEntries.length })}
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose}>{t("pages.customerView.present.cancel")}</Button>
          <Button color="dark" onClick={finish}>{t("pages.customerView.present.closeAndBack")}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
